import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import proptypes from 'prop-types';
import { Container, IconButton } from '@mui/material';
import { CameraAlt as CameraAltIcon, Cameraswitch as CameraswitchIcon, Close as CloseIcon } from '@mui/icons-material';
import Quagga from '@ericblade/quagga2';
import QrCodeReader from '@ericblade/quagga2-reader-qr';
import parseStringCode from '../utils/parseCodeString';

Quagga.registerReader('qrcode', QrCodeReader);

function Scan({ onChange }) {
  const isScanning = useSelector((state) => state.scanner.scanning);
  const isFrontCamera = useSelector((state) => state.scanner.frontCamera);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [videoDevices, setVideoDevices] = useState([]);

  const handleStop = () => {
    dispatch({ type: 'scanner/stop' });
  };

  const handleSwitch = () => {
    dispatch({ type: 'scanner/stop' });
    dispatch({ type: 'scanner/switchCamera' });
    dispatch({ type: 'scanner/start' });
  };

  const handleClick = () => {
    dispatch({ type: 'scanner/start' });
  };

  const onDetect = useCallback(
    (res) => {
      const parsed = parseStringCode(res.codeResult.code);
      parsed.lots = parsed.attributes.map((attribute) => {
        return { authority: attribute.key, code: attribute.value, ean: parsed.code };
      });
      dispatch({ type: 'scanner/productDetected', payload: parsed.code });
      if (parsed.lots.length > 0) dispatch({ type: 'scanner/lotsDetected', payload: parsed.lots });
      dispatch({ type: 'scanner/stop' });
      onChange(parsed);
    },
    [isScanning]
  );

  async function enumerateDevicesForScan() {
    if (navigator?.mediaDevices?.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const tmpVideoDevices = devices.filter((device) => device.kind === 'videoinput');
      setVideoDevices(tmpVideoDevices);
    }
  }

  useEffect(() => {
    enumerateDevicesForScan();
  }, [isScanning]);

  useEffect(() => {
    if (isScanning) {
      Quagga.init(
        {
          inputStream: {
            name: 'Live',
            type: 'LiveStream',
            constraints: {
              width: 640,
              height: 480,
              deviceId: isFrontCamera ? videoDevices[videoDevices.length - 1]?.deviceId : videoDevices[0]?.deviceId,
            },
            numberOfWorkers: navigator.hardwareConcurrency,
            target: document.querySelector('#modalOverlayBarcodeScan'),
          },
          locate: true,
          decoder: {
            readers: ['ean_reader', 'ean_8_reader', 'code_128_reader', 'code_39_reader', 'qrcode'],
          },
        },
        (err) => {
          if (err) {
            console.log(err);
            return;
          }
          if (isScanning) {
            Quagga.start();
          }
        }
      );
      Quagga.onDetected(onDetect);
    }
    return () => {
      Quagga.offProcessed();
      Quagga.offDetected();
      Quagga.stop();
    };
  }, [isScanning, isFrontCamera]);

  if (!isScanning) {
    return (
      <IconButton color="secondary" role="scan" onClick={handleClick}>
        <CameraAltIcon />
      </IconButton>
    );
  }

  return (
    <>
      <IconButton color="secondary" role="scan" onClick={handleClick}>
        <CameraAltIcon />
      </IconButton>
      <div id="modalOverlayBarcodeScan">
        <Container maxWidth="lg">
          <div id="barcodeScanHeader">
            <div id="barcodeScanTitle">{t('common.actions.scanBarcode')}</div>
            <div onClick={handleStop} onKeyPress={handleStop} tabIndex={0} role="button" type="button" id="overlayClose" className="overlayIcon">
              <CloseIcon />
            </div>
          </div>
          <div onClick={handleSwitch} onKeyPress={handleSwitch} tabIndex={0} role="button" type="button" id="overlaySwitch" className="overlayIcon">
            <CameraswitchIcon />
          </div>
        </Container>
      </div>
    </>
  );
}

Scan.propTypes = {
  onChange: proptypes.func.isRequired,
};

export default Scan;
